import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Factory, LogIn, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

export default function LoginPage() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  if (user && user !== false) return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email.trim(), password);
      toast.success("Bem-vindo ao TheoKratos RH");
      nav("/dashboard");
    } catch (ex) {
      const msg = formatApiError(ex.response?.data?.detail) || "Falha ao autenticar";
      setErr(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="grid min-h-screen grid-cols-1 lg:grid-cols-2">
      {/* Left visual */}
      <div className="rh-login-bg relative hidden lg:flex flex-col justify-between p-12 text-white">
        <div className="flex items-center gap-3">
          <div className="grid h-11 w-11 place-items-center rounded-md bg-white/10 backdrop-blur-sm ring-1 ring-white/20">
            <Factory className="h-5 w-5 text-[#C5A059]" />
          </div>
          <div className="leading-tight">
            <div className="font-display text-[18px] font-semibold tracking-wide">TheoKratos <span className="text-[#C5A059]">RH</span></div>
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-white/70">Plataforma Inteligente</div>
          </div>
        </div>

        <div className="max-w-md">
          <div className="text-[10.5px] uppercase tracking-[0.24em] text-[#C5A059] font-medium">Plataforma Corporativa</div>
          <h2 className="font-display text-4xl md:text-5xl font-semibold leading-[1.05] mt-4">
            Gestão inteligente<br />
            <span className="text-[#C5A059]">de pessoas</span>, do quadro<br />
            ao estratégico.
          </h2>
          <p className="mt-5 text-[14px] leading-relaxed text-white/75">
            Recrutamento, treinamentos de liderança, quadro por setor, plano de cargos e salários,
            importação em massa e dashboards executivos — tudo em um só lugar.
          </p>
        </div>

        <div className="flex items-end justify-between text-[11px] text-white/60 uppercase tracking-[0.18em]">
          <span>Usina ALCON · NDB Holding · Sayonara Mineração</span>
          <span>© 2026</span>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 md:p-12 bg-background">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="grid h-10 w-10 place-items-center rounded-md bg-[#234935]">
              <Factory className="h-5 w-5 text-[#C5A059]" />
            </div>
            <div>
              <div className="font-display text-[17px] font-semibold">TheoKratos <span className="text-[#8A6B2B]">RH</span></div>
              <div className="text-[10.5px] uppercase tracking-[0.22em] text-muted-foreground">Plataforma Inteligente</div>
            </div>
          </div>

          <div className="text-[11px] uppercase tracking-[0.24em] text-[#8A6B2B] font-medium">Acesso ao sistema</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold mt-2">Entrar na plataforma</h1>
          <p className="mt-2 text-[13.5px] text-muted-foreground">
            Informe suas credenciais corporativas para continuar.
          </p>

          <form onSubmit={submit} className="mt-7 space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-[11px] uppercase tracking-wider">E-mail</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="seu.email@alcon.com.br"
                required
                data-testid="login-email-input"
                className="h-11"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-[11px] uppercase tracking-wider">Senha</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={show ? "text" : "password"}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  data-testid="login-password-input"
                  className="h-11 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShow((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  data-testid="login-toggle-password"
                >
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {err && (
              <div data-testid="login-error" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[12.5px] text-red-700">
                {err}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="h-11 w-full bg-[#234935] hover:bg-[#2c5c43] text-white font-medium tracking-wide"
            >
              <LogIn className="h-4 w-4 mr-2" />
              {loading ? "Entrando..." : "Entrar"}
            </Button>

            <div className="relative py-1">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-neutral-200" />
              </div>
              <div className="relative flex justify-center text-[10.5px] uppercase tracking-[0.22em]">
                <span className="bg-background px-2 text-muted-foreground">ou</span>
              </div>
            </div>

            <button
              type="button"
              onClick={handleGoogleLogin}
              data-testid="login-google-btn"
              className="h-11 w-full inline-flex items-center justify-center gap-2 rounded-md border border-neutral-300 bg-white text-neutral-800 hover:bg-neutral-50 text-[13.5px] font-medium tracking-wide"
            >
              <svg className="h-4 w-4" viewBox="0 0 48 48" aria-hidden="true">
                <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.9 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.1 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"/>
                <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.1 6.1 29.3 4 24 4 16.3 4 9.7 8.4 6.3 14.7z"/>
                <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.1 34.9 26.7 36 24 36c-5.3 0-9.7-3.1-11.3-8l-6.5 5C9.5 39.5 16.2 44 24 44z"/>
                <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4.2 5.6l6.2 5.2c-.4.4 6.7-4.9 6.7-14.8 0-1.2-.1-2.3-.4-3.5z"/>
              </svg>
              Entrar com Google
            </button>

            <div className="text-center pt-1">
              <a href="/esqueci-senha" className="text-[12px] text-muted-foreground hover:text-[#234935] hover:underline" data-testid="login-forgot-link">
                Esqueci minha senha
              </a>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
